<script setup lang="ts">
import { passwordSymbolRegex } from "~/constants/regex.constants";

defineEmits(["update:modelValue"]);

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    required: false,
    default: "",
  },
  passwordRegexFail: {
    type: String,
    required: false,
    default: "",
  },
  passwordsMatchFail: {
    type: String,
    required: false,
    default: "",
  },
  id: {
    type: String,
    required: true,
  },
  tooltip: {
    type: Boolean,
    required: false,
    default: false,
  },
});
const text = ref<string>("");

function setErrorStyle() {
  if (props.passwordRegexFail) {
    return "border-b-2 border-error-300 focus:border-error-300 focus:ring-error-200 bg-secondary-900";
  } else if (props.passwordsMatchFail) {
    return "border-b-2 border-error-300 focus:border-error-300 focus:ring-error-200 bg-secondary-900";
  } else {
    return "border-b-2 border-b-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-0 border-t-0 border-r-0 border-l-0";
  }
}

const showPassword = ref<boolean>(false);
const hasLength = ref<boolean>(false);
const hasLetters = ref<boolean>(false);
const hasNumbers = ref<boolean>(false);
const hasSymbols = ref<boolean>(false);

const checkReqs = () => {
  if (text.value.length >= 8) {
    hasLength.value = true;
  } else {
    hasLength.value = false;
  }

  if (/^(?=.*[a-z])(?=.*[A-Z])/.test(text.value)) {
    hasLetters.value = true;
  } else {
    hasLetters.value = false;
  }

  if (passwordSymbolRegex.test(text.value)) {
    hasSymbols.value = true;
  } else {
    hasSymbols.value = false;
  }

  if (/[0-9]/.test(text.value)) {
    hasNumbers.value = true;
  } else {
    hasNumbers.value = false;
  }
};
watch(text, checkReqs);
</script>

<template>
  <div class="select-none relative">
    <label
      :for="id"
      class="block text-sm font-medium leading-6 text-neutral-300">
      {{ label }}
    </label>
    <div class="relative mt-1" :class="setErrorStyle()">
      <input
        :id="id"
        v-model="text"
        :name="id"
        :type="showPassword ? 'text' : 'password'"
        :placeholder="placeholder"
        data-test="input-password"
        autocomplete="current-password"
        required
        class="peer w-full py-3 px-4 pr-9 bg-white text-neutral-500 placeholder:text-gray-400 rounded-t focus:ring-transparent focus:border-transparent border-0"
        @change="
          $emit(
            'update:modelValue',
            ($event.target as HTMLTextAreaElement).value
          )
        " />
      <CommonTooltip
        v-if="tooltip"
        position="custom"
        custom-class="w-[100%] translate-x-1/2 right-1/2 top-[3.8rem] lg:w-max lg:translate-x-52 lg:left-1/2 lg:-top-12 absolute p-2 bg-neutral-200 hidden peer-focus:flex flex-col justify-center items-center flex-nowrap rounded-sm z-10"
        custom-arrow-class="-top-36 -translate-x-2/4 left-2/4 rotate-180 lg:-top-[4.2rem] lg:-left-3 lg:rotate-90">
        <template #tooltip-text>
          <div class="flex flex-col gap-2 p-2 mr-auto lg:mr-0">
            <p class="text-xs text-black font-semibold">
              {{ $t("main.inputPassword.yourPass") }}
            </p>
            <div class="flex flex-col gap-2">
              <div class="flex gap-2">
                <BaseIcon
                  :name="hasLetters ? 'checkmark_green' : 'checkmark_red'"
                  class="w-4 h-4"
                  :class="hasLetters ? 'text-success-300' : 'text-error-400'" />
                <p
                  class="text-xs"
                  :class="hasLetters ? 'line-through' : 'text-black'">
                  {{ $t("main.inputPassword.uppercaseAndLowercaseLetters") }}
                </p>
              </div>
              <div class="flex gap-2">
                <BaseIcon
                  :name="hasSymbols ? 'checkmark_green' : 'checkmark_red'"
                  class="w-4 h-4"
                  :class="hasSymbols ? 'text-success-300' : 'text-error-400'" />
                <p
                  class="text-xs"
                  :class="hasSymbols ? 'line-through' : 'text-black'">
                  {{ $t("main.inputPassword.aSymbol") }}
                </p>
              </div>
              <div class="flex gap-2">
                <BaseIcon
                  :name="hasNumbers ? 'checkmark_green' : 'checkmark_red'"
                  class="w-4 h-4"
                  :class="hasNumbers ? 'text-success-300' : 'text-error-400'" />
                <p
                  class="text-xs"
                  :class="hasNumbers ? 'line-through' : 'text-black'">
                  {{ $t("main.inputPassword.aNumber") }}
                </p>
              </div>
              <div class="flex gap-2">
                <BaseIcon
                  :name="hasLength ? 'checkmark_green' : 'checkmark_red'"
                  class="w-4 h-4"
                  :class="hasLength ? 'text-success-300' : 'text-error-400'" />
                <p
                  class="text-xs"
                  :class="hasLength ? 'line-through' : 'text-black'">
                  {{ $t("main.inputPassword.beAtLeast8CharactersLong") }}
                </p>
              </div>
            </div>
          </div>
        </template>
      </CommonTooltip>
      <div
        data-test="button-eye-input-password"
        class="cursor-pointer absolute right-0 top-3.5 pr-3"
        style="background-color: transparent">
        <NuxtIcon
          v-if="!showPassword"
          name="mdi:eye-off"
          class="h-5 w-5 text-gray-400 cursor-pointer"
          aria-hidden="true"
          @click="showPassword = !showPassword" />
        <NuxtIcon
          v-else
          name="mdi:eye"
          class="h-5 w-5 text-gray-400 cursor-pointer"
          aria-hidden="true"
          @click="showPassword = !showPassword" />
      </div>
    </div>
    <p class="mt-2 text-error-300 text-xs">
      {{ passwordRegexFail }}
    </p>
    <p class="mt-2 text-error-300 text-xs">
      {{ passwordsMatchFail }}
    </p>
  </div>
</template>
